import React from 'react';
import cn from "classnames";
import copy from 'clipboard-copy'
import { actions, useStore } from "../store";
import { convertHtmlToText } from "../lib/utils/convert";
import { cleanupFormValue } from "../lib/utils/form";
import request from "../api/request";
import { WizardButtons } from "../Wizard";
import { CopyHtmlButton, CopyTextButton, LoginButton, PrevButton, PricingButton, ResetButton } from "../Shared/Buttons";
import InnerHtml from "../Shared/InnerHtml";
import Alert from "../Shared/Alert";
import LanguageSelect, { defaultLanguage } from "../Shared/LanguageSelect";

const INFO_ALERT = {
  type: 'secondary',
  body: `CookieScript is not a law firm and is not providing legal advice. 
  All information generated with our Privacy Policy Generator is provided without any warranty, express or implied, 
  including as to their legal effect and completeness. The information should be used as a guide and modified to meet 
  your own individual needs and the laws of your state.`
}

const PreviewStep = () => {
  const [isLoading, setLoading] = React.useState(true)
  const [currentLang, setCurrentLang] = React.useState(defaultLanguage)
  const [isCopyHtml, setCopyHtml] = React.useState(false)
  const [isCopyText, setCopyText] = React.useState(false)
  const [responseData, setResponseData] = React.useState({})
  const setStopLoadLanguage = useStore(state => state.setStopLoadLanguage)
  const contactsFormValue = useStore(state => state.contactsFormValue)
  const informationFormValue = useStore(state => state.informationFormValue)
  const usageFormValue = useStore(state => state.usageFormValue)
  const extrasFormValue = useStore(state => state.extrasFormValue)
  let formsValue = {
    contactsFormValue, informationFormValue, usageFormValue, extrasFormValue
  }

  formsValue = cleanupFormValue(formsValue)
  if(window.ppGenerate === 1) {
    console.log(formsValue)
  }

  const onChangeLang = lang => {
    if(lang.value !== currentLang.value) {
      setStopLoadLanguage(currentLang.value)
      setCurrentLang(lang)
      setLoading(true)
    }
  }

  const copyHtmlHandle = () => {
    copy(responseData.view).then(() => {
      setCopyHtml(true)
      setTimeout(() => {
        setCopyHtml(false)
      }, 3000)
    }).catch(error => {
      console.log(error, 'error');
    })
  }

  const copyTextHandle = () => {
    const text = convertHtmlToText(responseData.view)
    copy(text).then(() => {
      setCopyText(true)
      setTimeout(() => {
        setCopyText(false)
      }, 3000)
    }).catch(error => {
      console.log(error, 'error');
    })
  }

  const getPolicyContent = (isSecondGenerate = false ) => {
    const currentStep = useStore.getState().currentStep
    const stopLoadLanguage = useStore.getState().stopLoadLanguage
    formsValue.currentLang = currentLang.value
    formsValue.isSaveStatistic = true

    if(stopLoadLanguage === currentLang.value) {
      return
    }

    if(currentStep.name === 'preview') {
      if(isSecondGenerate) {
        formsValue.isSaveStatistic = false
      }
      request.post('api/privacy-policy/ui/generate', formsValue)
        .then(response => {
          setResponseData(response.data)
          setLoading(false)
          if(response.data.isPreview && currentStep.name === 'preview') {
            setTimeout(() => {
              getPolicyContent(true)
            }, 2000)
          }
        })
        .catch(error => {
          console.log(error, 'error');
        })
    }
  }

  React.useEffect(() => {
    setStopLoadLanguage(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  React.useEffect(() => {
    getPolicyContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentLang])

  return (
    <div className="card shadow-lg">
      <div className="card-header">
        <h3 className="card-title">
          { isLoading
            ? 'Your Privacy Policy is generated...'
            : `Your Privacy Policy for ${formsValue.contactsFormValue.website} is ready`
          }
        </h3>
        { !isLoading &&
          <div className="card-toolbar">
            <LanguageSelect selected={currentLang} onChange={onChangeLang} />
            <CopyHtmlButton onClick={copyHtmlHandle} disabled={responseData.isPreview} isCopied={isCopyHtml} />
            <CopyTextButton
              onClick={copyTextHandle}
              disabled={responseData.isPreview}
              isCopied={isCopyText}
              cookieId={formsValue.extrasFormValue.cookieId}
            />
          </div>
        }
      </div>
      <div className="card-body min-vh-30">
        { isLoading
          ?
          <div className='overlay overlay-block card-rounded'>
            <div className='overlay-wrapper p-5 min-vh-30 d-flex justify-content-center align-items-center'>
              <b className='fs-2'>Loading...</b>
            </div>
            <div className='overlay-layer card-rounded bg-dark bg-opacity-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          </div>
          :
          <>
            {!responseData.isPreview && <Alert  alert={INFO_ALERT}/>}
            <InnerHtml html={responseData.view} className={cn(
              {
                'is-preview': responseData.isPreview,
                'rtl' : ['ar'].includes(currentLang.value)
              }
            )}
            />

            { responseData.isPreview &&
              <div className="card shadow-lg w-50 m-auto">
                <div className="card-body d-flex align-items-center justify-content-center text-center">
                  <div className='fs-4 fw-bold'>
                    {!responseData.isUser &&
                      'Sign in to show the full text of your Privacy Policy'
                    }
                    {responseData.isUser && !responseData.isAccess &&
                      'Upgrade your account to show the full text of your Privacy Policy'
                    }
                  </div>
                </div>
                <div className='card-footer'>
                  <div className='d-flex justify-content-center'>
                    {!responseData.isUser &&
                      <>
                        <LoginButton />
                        <PricingButton title='Show Pricing plans' />
                      </>
                    }
                    {responseData.isUser && !responseData.isAccess &&
                      <PricingButton title='Upgrade my account' />
                    }
                  </div>
                </div>
              </div>
            }
          </>
        }
      </div>
      <div className='card-footer'>
        <WizardButtons>
          <PrevButton onClick={() => actions.handleWizardPrevStep()} />
          <ResetButton title='New' onClick={() => actions.handleWizardReset()} />
        </WizardButtons>
      </div>
    </div>
  );
}

export default PreviewStep;
